import { useEffect, useRef, useState } from 'react';

import { type LearnTask } from '@apps/web/features/learn_phoebe/model/learn_content';
import {
    type LearnPhoebeSurface,
    useLearnPhoebeAnalytics,
} from '@apps/web/features/learn_phoebe/model/use_learn_phoebe_analytics';
import { toast } from '@apps/web/lib/app_notifications';

const COPIED_RESET_MS = 1600;

type CopyPromptController = {
    readonly isCopied: boolean;
    readonly copyPrompt: (text: string) => void;
};

// Copies a prompt and flips to "Copied" for a moment. One instance per control.
export function useCopyPrompt({
    surface,
    taskId,
    stepId,
}: {
    readonly surface: LearnPhoebeSurface;
    readonly taskId: LearnTask['id'];
    readonly stepId: string | null;
}): CopyPromptController {
    const [isCopied, setIsCopied] = useState(false);
    const { capturePromptCopied } = useLearnPhoebeAnalytics();
    const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        return () => {
            if (resetTimerRef.current !== null) {
                clearTimeout(resetTimerRef.current);
            }
        };
    }, []);

    function copyPrompt(text: string): void {
        void (async () => {
            try {
                await navigator.clipboard.writeText(text);
            } catch {
                // Clipboard access can be blocked; the prompt is still on
                // screen to select, and "Try in chat" puts it in the composer.
                toast.error('Couldn’t copy. Select the text instead.', {
                    persistNotification: false,
                });
                return;
            }
            // Captured only on success: a blocked clipboard is not a copy.
            capturePromptCopied({ surface, taskId, stepId });
            setIsCopied(true);
            if (resetTimerRef.current !== null) {
                clearTimeout(resetTimerRef.current);
            }
            resetTimerRef.current = setTimeout(() => {
                setIsCopied(false);
            }, COPIED_RESET_MS);
        })();
    }

    return { isCopied, copyPrompt };
}
